
import React from "react";
import { Search, MapPin, ShieldCheck, Star, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ReloadIcon } from "@radix-ui/react-icons";
import { StarRating } from "@/components/StarRating";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DealerFilterState } from "@/hooks/dealers/useDealers";

interface DealersFilterProps {
  filters: DealerFilterState;
  onUpdateFilters: (filters: Partial<DealerFilterState>) => void;
  onRefresh: () => void;
  isLoading: boolean;
}

export function DealersFilter({
  filters,
  onUpdateFilters,
  onRefresh,
  isLoading,
}: DealersFilterProps) {
  const handleMinRatingChange = (value: number) => {
    onUpdateFilters({ range: [value, filters.range[1]] });
  };

  const handleMaxRatingChange = (value: number) => {
    onUpdateFilters({ range: [filters.range[0], value] });
  };

  // Opzioni di distanza disponibili in km
  const distanceOptions = [0, 5, 10, 20, 50, 100];

  // Helper function to format distance display
  const formatDistanceLabel = (distance: number) => {
    return distance === 0 ? "Tutti" : `${distance} km`;
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4">
        {/* Search Bar */}
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Cerca per nome..."
            value={filters.search}
            onChange={(e) => onUpdateFilters({ search: e.target.value })}
            className="pl-9 w-full"
          />
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Distance Filter */}
          <Select
            value={filters.distanceRange.toString()}
            onValueChange={(value) => onUpdateFilters({ distanceRange: parseInt(value) })}
          >
            <SelectTrigger className="w-[130px] h-9 flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Distanza">
                {formatDistanceLabel(filters.distanceRange)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {distanceOptions.map((option) => (
                <SelectItem key={option} value={option.toString()}>
                  {formatDistanceLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort Order */}
          <Select
            value={filters.sortOrder}
            onValueChange={(value) => onUpdateFilters({ sortOrder: value as "asc" | "desc" | "distance" })}
          >
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue placeholder="Ordina per" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="distance">Distanza più vicina</SelectItem>
              <SelectItem value="asc">Valutazione crescente</SelectItem>
              <SelectItem value="desc">Valutazione decrescente</SelectItem>
            </SelectContent>
          </Select>

          {/* Advanced Filters Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1">
                <Filter className="h-4 w-4" />
                Filtri
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4">
              <div className="space-y-4">
                <h4 className="font-medium">Filtri avanzati</h4>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="verified"
                      checked={filters.filterVerified}
                      onCheckedChange={(checked) =>
                        onUpdateFilters({ filterVerified: checked })
                      }
                    />
                    <Label htmlFor="verified">Solo verificati</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="activated"
                      checked={filters.filterActivated}
                      onCheckedChange={(checked) =>
                        onUpdateFilters({ filterActivated: checked })
                      }
                    />
                    <Label htmlFor="activated" className="flex items-center gap-1">
                      <ShieldCheck className="h-4 w-4 text-metal" />
                      Solo attivati
                    </Label>
                  </div>
                </div>
                
                <div>
                  <Label className="block mb-2">Valutazione:</Label>
                  <StarRating
                    minValue={filters.range[0]}
                    maxValue={filters.range[1]}
                    onMinChange={handleMinRatingChange}
                    onMaxChange={handleMaxRatingChange}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Refresh Button */}
          <Button 
            variant="outline" 
            size="sm"
            disabled={isLoading} 
            onClick={onRefresh}
            className="h-9 ml-auto"
          >
            {isLoading ? (
              <>
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                Aggiornamento...
              </>
            ) : (
              "Aggiorna"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
